import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verifyOTP } from '../../Store/actions/userActions';
import OTPService from '../../services/otpService';
import './OTPVerification.css';

const OTPVerification = ({ onVerificationComplete }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { loading, error, isAuthenticated, user } = useSelector(state => state.user);
  
  // Get email from route state, falling back to the logged-in user's email
  const email = location.state?.email || user?.email || 'your-email@example.com';
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [resendStatus, setResendStatus] = useState('idle'); // 'idle', 'sending', 'success', 'error'
  const [resendMessage, setResendMessage] = useState('');
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [isResending, setIsResending] = useState(false);

  // Auto-focus next input when digit is entered
  const inputRefs = [
    React.useRef(),
    React.useRef(),
    React.useRef(),
    React.useRef(),
    React.useRef(),
    React.useRef()
  ];

  // Enhanced countdown timer with better UX
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            setResendStatus('idle');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [resendTimer]);

  // Initialize timer on component mount
  useEffect(() => {
    setResendTimer(60);
    setCanResend(false);
    setResendStatus('idle');
  }, []);

  // Redirect once the user is authenticated AND verified.
  // Authenticated-but-unverified users must stay here (ProtectedRoute
  // redirects them back to this view otherwise).
  useEffect(() => {
    if (isAuthenticated && !loading && user?.isVerified) {
      if (onVerificationComplete) {
        onVerificationComplete();
      } else {
        navigate(user.hasCompletedOnboarding === true ? '/dashboard' : '/pre-onboard');
      }
    }
  }, [isAuthenticated, loading, user, navigate, onVerificationComplete]);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all digits are filled
    if (newOtp.every(digit => digit !== '') && index === 5) {
      handleVerifyOTP();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) return;

    try {
      setOtpAttempts(prev => prev + 1);
      // Navigation is handled by the effect above once the store updates
      await dispatch(verifyOTP({ email, otp: otpString }));
    } catch (error) {
      console.error('OTP verification failed:', error);
      // Clear OTP on failed attempt
      setOtp(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
    }
  };

  const handleResendOTP = useCallback(async () => {
    if (!canResend || isResending) return;

    try {
      setIsResending(true);
      setResendStatus('sending');
      setResendMessage('Sending new code...');

      // Call actual resend OTP API
      await OTPService.resendOTP(email);
      
      // Reset timer and state
      setCanResend(false);
      setResendTimer(60);
      setResendStatus('success');
      setResendMessage('New code sent! Check your email.');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setResendStatus('idle');
        setResendMessage('');
      }, 3000);

    } catch (error) {
      console.error('Failed to resend OTP:', error);
      setResendStatus('error');
      setResendMessage('Failed to send code. Please try again.');
      setCanResend(true); // Allow retry
    } finally {
      setIsResending(false);
    }
  }, [canResend, isResending]);

  return (
    <div className="otp-verification-container">
      <div className="otp-verification-card">
        <div className="otp-header">
          <h2>Verify Your Email</h2>
          <p>We've sent a 6-digit code to <strong>{email}</strong></p>
          {otpAttempts > 0 && (
            <p className="attempt-warning">
              {otpAttempts === 1 && "1 attempt used"}
              {otpAttempts > 1 && `${otpAttempts} attempts used`}
            </p>
          )}
        </div>

        <div className="otp-input-container">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={inputRefs[index]}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              className="otp-input"
              placeholder="•"
            />
          ))}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <button
          className="verify-button"
          onClick={handleVerifyOTP}
          disabled={loading || otp.join('').length !== 6}
        >
          {loading ? 'Verifying...' : 'Verify Email'}
        </button>

        <div className="resend-section">
          <p>Didn't receive the code?</p>
          
          {/* Resend status messages */}
          {resendMessage && (
            <div className={`resend-message ${resendStatus}`}>
              {resendStatus === 'sending' && '⏳ '}
              {resendStatus === 'success' && '✅ '}
              {resendStatus === 'error' && '❌ '}
              {resendMessage}
            </div>
          )}

          {canResend ? (
            <button
              className="resend-button"
              onClick={handleResendOTP}
              disabled={loading || isResending}
            >
              {isResending ? 'Sending...' : 'Resend Code'}
            </button>
          ) : (
            <div className="resend-timer-container">
              <p className="resend-timer">
                Resend code in <span className="timer-count">{resendTimer}</span> seconds
              </p>
              <div className="timer-progress">
                <div 
                  className="timer-bar" 
                  style={{ width: `${((60 - resendTimer) / 60) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="back-section">
          <button
            className="back-button"
            onClick={() => navigate('/register')}
          >
            ← Back to Registration
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
